// Cmd-K global search palette.
//
// Aggregates every navigable surface — Features, published Releases, and
// Feature Announcements — into one substring-searchable list. Ranking is
// dumb-but-good-enough: startsWith name > contains name > matches any
// searchable field. Enter opens the top result; ↑/↓ navigate; Esc closes.
//
// Hotkey (⌘K / Ctrl-K) is bound at App level.
import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "wouter";
import type { EnrichedFeature, FeedItem } from "@release-hub/shared";
import { useFeatures, useFeed } from "@/api/queries";
import { useEffectiveRole } from "@/auth/role";
import { useTrack } from "@/lib/track";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/ui/dialog";
import { cn } from "@/lib/cn";
import { fmtDate } from "@/lib/format";

interface CommandResult {
  id: string;
  href: string;
  title: string;
  subtitle: string;
  kind: "feature" | "release" | "announcement";
  /** Concatenated searchable text — lowercased. */
  haystack: string;
}

function featureResult(f: EnrichedFeature): CommandResult {
  return {
    id: `feature:${f.id}`,
    href: `/features/${f.id}`,
    title: f.name,
    subtitle: [
      f.feature_family,
      f.status === "draft" ? "draft" : null,
      f.rollout.label,
    ]
      .filter(Boolean)
      .join(" · "),
    kind: "feature",
    haystack: [
      f.name,
      f.pitch,
      f.feature_family,
      f.audience,
      f.announcement_narrative,
      f.links_notes,
      f.web_note,
      f.mobile_note,
      ...f.known_gaps,
      ...f.flag_ids,
      f.owner_email,
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase(),
  };
}

function feedResult(item: FeedItem): CommandResult | null {
  if (item.type === "release") {
    const r = item.data;
    const primary = r.builds?.[0];
    const tag = primary?.tag_name ?? r.title ?? "Untitled";
    const dateStr = r.published_at ? fmtDate(r.published_at) : "";
    return {
      id: `release:${r.id}`,
      href: `/releases/${r.id}`,
      title: tag,
      subtitle: [primary?.repo, dateStr].filter(Boolean).join(" · "),
      kind: "release",
      haystack: [
        tag,
        r.narrative,
        r.title,
        primary?.repo,
        ...(r.items ?? []).flatMap((it) => [
          it.text,
          it.parity_notes,
          ...it.linear_ticket_ids,
        ]),
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase(),
    };
  }
  // Feature announcements de-dupe against featureResult above via the
  // caller — we key by id so the feature card wins over the announcement.
  return null;
}

/** Rank a result against the query. Higher = better. 0 = doesn't match. */
function score(query: string, r: CommandResult): number {
  if (!query) return 1; // empty query → everything ties
  const t = r.title.toLowerCase();
  if (t.startsWith(query)) return 100;
  if (t.includes(query)) return 50;
  return r.haystack.includes(query) ? 10 : 0;
}

export function CommandPalette({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
}) {
  const [, navigate] = useLocation();
  const { isEditor } = useEffectiveRole();
  const features = useFeatures();
  const feed = useFeed();
  const track = useTrack();

  const [query, setQuery] = useState("");
  const [cursor, setCursor] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);

  // Reset UI state each time the palette opens.
  useEffect(() => {
    if (open) {
      setQuery("");
      setCursor(0);
      // Focus after Radix's animation stabilizes to avoid a caret jump.
      requestAnimationFrame(() => inputRef.current?.focus());
    }
  }, [open]);

  const allResults = useMemo<CommandResult[]>(() => {
    const seen = new Set<string>();
    const out: CommandResult[] = [];
    // Features first — they include drafts (visible to editors) and are
    // the primary "what did we launch" surface.
    for (const f of features.data ?? []) {
      if (!isEditor && f.status !== "published") continue;
      const r = featureResult(f);
      if (seen.has(r.id)) continue;
      seen.add(r.id);
      out.push(r);
    }
    // Releases from the feed (published only — the feed already filters).
    for (const item of feed.data ?? []) {
      const r = feedResult(item);
      if (!r || seen.has(r.id)) continue;
      seen.add(r.id);
      out.push(r);
    }
    return out;
  }, [features.data, feed.data, isEditor]);

  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    return allResults
      .map((r) => ({ r, s: score(q, r) }))
      .filter((x) => x.s > 0)
      .sort((a, b) => b.s - a.s || a.r.title.localeCompare(b.r.title))
      .slice(0, 20)
      .map((x) => x.r);
  }, [allResults, query]);

  // Clamp cursor within results.
  useEffect(() => {
    if (cursor >= results.length) setCursor(0);
  }, [results.length, cursor]);

  const go = (r: CommandResult) => {
    track({
      event: "search",
      meta: { query: query.trim(), chosen_kind: r.kind, chosen_id: r.id },
    });
    onOpenChange(false);
    navigate(r.href);
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setCursor((c) => (results.length === 0 ? 0 : (c + 1) % results.length));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setCursor((c) => (results.length === 0 ? 0 : (c - 1 + results.length) % results.length));
    } else if (e.key === "Enter") {
      e.preventDefault();
      const chosen = results[cursor];
      if (chosen) go(chosen);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl p-0">
        <DialogHeader className="sr-only">
          <DialogTitle>Search</DialogTitle>
          <DialogDescription>
            Search features, releases, and announcements.
          </DialogDescription>
        </DialogHeader>
        <div className="border-b border-border px-4 py-3">
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setCursor(0);
            }}
            onKeyDown={onKeyDown}
            placeholder="Search features, releases, announcements…"
            className="w-full bg-transparent text-base placeholder:text-text-tertiary focus:outline-none"
          />
        </div>
        <div className="max-h-[60vh] overflow-y-auto p-2">
          {results.length === 0 ? (
            <p className="px-3 py-6 text-center text-sm text-text-tertiary">
              {allResults.length === 0
                ? "Loading…"
                : `No matches for "${query}".`}
            </p>
          ) : (
            <ul className="space-y-1">
              {results.map((r, i) => (
                <li key={r.id}>
                  <button
                    type="button"
                    onClick={() => go(r)}
                    onMouseEnter={() => setCursor(i)}
                    className={cn(
                      "flex w-full items-center justify-between gap-3 rounded px-3 py-2 text-left",
                      i === cursor
                        ? "bg-border/60"
                        : "hover:bg-border/40",
                    )}
                  >
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm text-text">
                        {r.title}
                      </span>
                      {r.subtitle && (
                        <span className="block truncate text-xs text-text-tertiary">
                          {r.subtitle}
                        </span>
                      )}
                    </span>
                    <span className="rounded-full bg-border/50 px-2 py-0.5 text-[10.5px] font-semibold uppercase tracking-wider text-text-tertiary">
                      {r.kind}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
        <div className="border-t border-border bg-bg/40 px-4 py-2 text-[11px] text-text-tertiary">
          <kbd className="rounded bg-border/60 px-1.5 py-0.5">↑</kbd>{" "}
          <kbd className="rounded bg-border/60 px-1.5 py-0.5">↓</kbd> navigate ·{" "}
          <kbd className="rounded bg-border/60 px-1.5 py-0.5">↵</kbd> open ·{" "}
          <kbd className="rounded bg-border/60 px-1.5 py-0.5">esc</kbd> close
        </div>
      </DialogContent>
    </Dialog>
  );
}

/** Global Cmd/Ctrl-K hotkey handler. Mount once inside App. */
export function useCommandPaletteHotkey(setOpen: (v: boolean) => void) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      // Don't hijack when the user is typing into an input/textarea/select
      // unless they use the modifier — Cmd-K wins even inside text fields.
      const isModified = e.metaKey || e.ctrlKey;
      if (isModified && (e.key === "k" || e.key === "K")) {
        e.preventDefault();
        setOpen(true);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [setOpen]);
}
